import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Failed to send message. Please try again or email hr@tri-nexa.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg radial-fade opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
            Contact
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            Let's build something <span className="text-gradient-cyan">intelligent</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Tell us about your idea, your team, and what success looks like.
            We typically respond within one business day.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          {/* INFO */}
          <div className="lg:col-span-5 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center">
                  <Mail className="text-[#00D4FF]" size={20} />
                </div>
                <div>
                  <div className="text-xs font-mono-tech tracking-widest uppercase text-slate-400">Email</div>
                  <a
                    href="mailto:hr@tri-nexa.com"
                    data-testid="contact-info-email"
                    className="text-white font-semibold hover:text-[#00D4FF] transition"
                  >
                    hr@tri-nexa.com
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center">
                  <Phone className="text-[#00D4FF]" size={20} />
                </div>
                <div>
                  <div className="text-xs font-mono-tech tracking-widest uppercase text-slate-400">Phone</div>
                  <a
                    href="tel:+919972625368"
                    data-testid="contact-info-phone"
                    className="text-white font-semibold hover:text-[#00D4FF] transition"
                  >
                    +91 99726 25368
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center">
                  <MapPin className="text-[#00D4FF]" size={20} />
                </div>
                <div>
                  <div className="text-xs font-mono-tech tracking-widest uppercase text-slate-400">Address</div>
                  <div data-testid="contact-info-address" className="text-white font-semibold">
                    Bangalore, Karnataka, India
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-[#0A1128] to-[#050B14] border border-[#00D4FF]/20">
              <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[#00D4FF] mb-2">
                Response Time
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                We're a small, senior team — every message is read and replied
                by an engineer within one business day.
              </p>
            </div>
          </div>

          {/* FORM */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 p-7 md:p-10 rounded-2xl bg-[#0A1128] border border-white/10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Your name *"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Jane Doe"
                testId="contact-input-name"
                required
              />
              <Field
                label="Email *"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                testId="contact-input-email"
                required
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="+91 ..."
                testId="contact-input-phone"
              />
              <Field
                label="Subject *"
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder="How can we help?"
                testId="contact-input-subject"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-mono-tech tracking-widest uppercase text-slate-400 mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                required
                placeholder="Tell us about your project, goals and timelines..."
                data-testid="contact-input-message"
                className="w-full px-4 py-3 rounded-lg bg-[#050B14] border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00D4FF]/60 focus:ring-1 focus:ring-[#00D4FF]/40 transition"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold hover:bg-[#33DDFF] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send message <Send size={16} />
                </>
              )}
            </button>

            <p className="text-xs text-slate-500">
              By submitting, you agree we may contact you at the email/phone
              you provide.
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-xs font-mono-tech tracking-widest uppercase text-slate-400 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-lg bg-[#050B14] border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00D4FF]/60 focus:ring-1 focus:ring-[#00D4FF]/40 transition"
      />
    </div>
  );
}
